import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import messages from '../../../locale/jsonMessages';
import { openDispute, addDisputeStatement } from '../../../actions/Inspection/inspection';
import { DISPUTE_REASONS, FORM_LIMITS, validateStatement } from '../../../helpers/inspectionForm';
import { disputeStatusKey, decisionLabelKey } from '../../../helpers/inspectionPresentation';
import { prepareAttachment } from '../../../helpers/inspectionUpload';
import usePhotoSeries from '../usePhotoSeries';
import InspectionBanner from '../InspectionBanner/InspectionBanner';
import s from '../Inspection.css';

const ROLE_KEYS = { host: 'inspRoleHost', guest: 'inspRoleGuest', admin: 'inspRoleAdmin' };
const attachmentUrl = (id) => `/inspection/photo/${id}`;
const when = (value, locale) => (value ? new Date(value).toLocaleString(locale) : '');

// Attachment picker (JPG / PNG / PDF, 5 MB). Used by the party statements here and by the admin decision form.
export function AttachmentPicker({ reservationId, series, label }) {
  const { formatMessage } = useIntl();
  return (
    <div className={s.field}>
      <span className={s.fieldLabel}>{label || formatMessage(messages.inspAttachments)}</span>
      <ul className={s.attachmentList}>
        {series.items.map((item, index) => (
          <li key={item.id !== undefined && item.id !== null ? `a${item.id}` : `n${item.localId}`} className={s.attachmentLink}>
            {item.status === 'uploading' && formatMessage(messages.inspUploading)}
            {item.status === 'failed' && <span role="alert">{formatMessage(messages[item.errorKey || 'inspUploadFailed'])}</span>}
            {item.status === 'ready' && <a href={attachmentUrl(item.id)} target="_blank" rel="noopener noreferrer">{formatMessage(messages.inspAttachmentN, { index: index + 1 })}</a>}
            <button type="button" className={s.photoRetry} onClick={() => series.remove(item)} aria-label={formatMessage(messages.inspPhotoRemove, { index: index + 1 })}>{'×'}</button>
          </li>
        ))}
      </ul>
      <label className={cx(s.button, s.buttonSecondary)} style={{ display: 'inline-block' }}>
        {formatMessage(messages.inspAddAttachment)}
        <input type="file" multiple accept="image/jpeg,image/png,application/pdf" className={s.hiddenInput}
          onChange={(event) => { const files = Array.from(event.target.files || []); event.target.value = ''; if (files.length) series.add(files); }} />
      </label>
      <p className={s.cardHint}>{formatMessage(messages.inspAttachmentsHint)}</p>
    </div>
  );
}

// Disagreement (R12) and dispute thread of a reservation, for the owner and the renter. Opening needs a predefined reason and a
// comment; afterwards each party adds statements with attachments (JPG / PNG / PDF, 5 MB). Once the administration decided, the
// dispute is read-only (notes are only displayed). GOZY is an intermediary and gives no insurance (R16).
export function DisputePanel({ reservationId, dispute, canOpen, viewerRole, onChanged }) {
  const { formatMessage, locale } = useIntl();
  const dispatch = useDispatch();
  const [opening, setOpening] = useState(false);
  const [reason, setReason] = useState('damage');
  const [comment, setComment] = useState('');
  const [statement, setStatement] = useState('');
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const series = usePhotoSeries({
    reservationId, type: 'attachment', kind: 'attachment', initial: [], prepare: prepareAttachment,
    onError: (failure) => setNotice({ tone: 'danger', text: failure.serverMessage || formatMessage(messages[failure.messageKey]) })
  });

  const failureText = (result) => (result && result.errorMessage) || formatMessage(messages.inspErrGeneric);

  const submitOpen = useCallback(async () => {
    const error = validateStatement(comment);
    if (error) { setNotice({ tone: 'danger', text: formatMessage(messages[error]) }); return; }
    setBusy(true); setNotice(null);
    const result = await dispatch(openDispute({ reservationId, reasonCode: reason, comment: comment.trim() }));
    if (result && result.status === 'success') { setOpening(false); setComment(''); await onChanged(); }
    else setNotice({ tone: 'danger', text: failureText(result) });
    setBusy(false);
  }, [comment, reason, reservationId]);

  const submitStatement = async () => {
    const error = validateStatement(statement);
    if (error) { setNotice({ tone: 'danger', text: formatMessage(messages[error]) }); return; }
    setBusy(true); setNotice(null);
    const result = await dispatch(addDisputeStatement({
      disputeId: dispute.id, content: statement.trim(), attachments: series.readyItems.map((item) => item.id)
    }));
    if (result && result.status === 'success') { setStatement(''); series.reset([]); await onChanged(); }
    else setNotice({ tone: 'danger', text: failureText(result) });
    setBusy(false);
  };

  if (!dispute && !canOpen) return null;

  return (
    <section className={s.card} aria-label={formatMessage(messages.inspDisputeTitle)}>
      <h2 className={s.cardTitle}>{formatMessage(messages.inspDisputeTitle)}</h2>
      {notice && <InspectionBanner tone={notice.tone}>{notice.text}</InspectionBanner>}

      {!dispute && !opening && (
        <>
          <p className={s.cardHint}>{formatMessage(messages.inspDisputeIntro)}</p>
          <div className={s.actions}>
            <button type="button" className={cx(s.button, s.buttonDanger)} onClick={() => setOpening(true)}>{formatMessage(messages.inspDisputeOpen)}</button>
          </div>
        </>
      )}

      {!dispute && opening && (
        <form noValidate onSubmit={(event) => event.preventDefault()}>
          <p className={s.infoStrip}>{formatMessage(messages.inspDisputeIntermediary)}</p>
          <div className={s.field}>
            <label className={s.fieldLabel} htmlFor="dispute-reason">{formatMessage(messages.inspDisputeReason)}</label>
            <select id="dispute-reason" className={s.select} value={reason} onChange={(event) => setReason(event.target.value)}>
              {DISPUTE_REASONS.map((item) => <option key={item.value} value={item.value}>{formatMessage(messages[item.labelKey])}</option>)}
            </select>
          </div>
          <div className={s.field}>
            <label className={s.fieldLabel} htmlFor="dispute-comment">{formatMessage(messages.inspDisputeComment)}</label>
            <textarea id="dispute-comment" className={s.textarea} value={comment} maxLength={FORM_LIMITS.statement} onChange={(event) => setComment(event.target.value)} />
            <p className={s.charCount}>{comment.length}/{FORM_LIMITS.statement}</p>
          </div>
          <div className={s.actions}>
            <button type="button" className={cx(s.button, s.buttonSecondary)} disabled={busy} onClick={() => { setOpening(false); setNotice(null); }}>{formatMessage(messages.inspCancel)}</button>
            <button type="button" className={s.button} disabled={busy} onClick={submitOpen}>{formatMessage(messages.inspDisputeSubmit)}</button>
          </div>
        </form>
      )}

      {dispute && (
        <>
          <p>
            <strong>{formatMessage(messages[disputeStatusKey(dispute.status)])}</strong>
            {' · '}{formatMessage(messages[(DISPUTE_REASONS.find((item) => item.value === dispute.reasonCode) || DISPUTE_REASONS[3]).labelKey])}
            {' · '}{when(dispute.createdAt, locale)}
          </p>
          <p className={s.infoStrip}>{formatMessage(messages.inspDisputeIntermediary)}</p>
          <h3 className={s.cardTitle}>{formatMessage(messages.inspDisputeStatements)}</h3>
          <ul className={s.thread}>
            {(dispute.statements || []).map((item) => (
              <li key={item.id} className={s.statement}>
                <p className={s.statementHead}><span>{formatMessage(messages[ROLE_KEYS[item.role] || 'inspRoleGuest'])}</span><span>{when(item.createdAt, locale)}</span></p>
                <p className={s.statementBody}>{item.content}</p>
                {item.attachments && item.attachments.length > 0 && (
                  <ul className={s.attachmentList}>
                    {item.attachments.map((id, index) => <li key={id}><a className={s.attachmentLink} href={attachmentUrl(id)} target="_blank" rel="noopener noreferrer">{formatMessage(messages.inspAttachmentN, { index: index + 1 })}</a></li>)}
                  </ul>
                )}
              </li>
            ))}
          </ul>

          {(dispute.decisions || []).map((item) => (
            <div key={item.id} className={cx(s.banner, s.toneInfo)}>
              <div>
                <p className={s.bannerTitle}>{formatMessage(messages.inspDecisionOfAdmin)}: {formatMessage(messages[decisionLabelKey(item.type)])}</p>
                <p className={s.bannerText}>{item.motive}</p>
                {item.retainedAmount > 0 && <p className={s.bannerText}>{formatMessage(messages.inspDecisionRetained)}: {item.retainedAmount} {item.currency}</p>}
                {item.requestedAmount > 0 && <p className={s.bannerText}>{formatMessage(messages.inspDecisionRequested)}: {item.requestedAmount} {item.currency}</p>}
              </div>
            </div>
          ))}

          {dispute.isActive && viewerRole !== 'admin' && (
            <form noValidate onSubmit={(event) => event.preventDefault()}>
              <div className={s.field}>
                <label className={s.fieldLabel} htmlFor="dispute-statement">{formatMessage(messages.inspDisputeAddStatement)}</label>
                <textarea id="dispute-statement" className={s.textarea} value={statement} maxLength={FORM_LIMITS.statement} onChange={(event) => setStatement(event.target.value)} />
                <p className={s.charCount}>{statement.length}/{FORM_LIMITS.statement}</p>
              </div>
              <AttachmentPicker reservationId={reservationId} series={series} />
              <div className={s.actions}>
                <button type="button" className={s.button} disabled={busy || series.busy} onClick={submitStatement}>{formatMessage(messages.inspDisputeSendStatement)}</button>
              </div>
            </form>
          )}
        </>
      )}
    </section>
  );
}

DisputePanel.propTypes = {
  reservationId: PropTypes.number.isRequired,
  dispute: PropTypes.object,
  canOpen: PropTypes.bool,
  viewerRole: PropTypes.string,
  onChanged: PropTypes.func
};
DisputePanel.defaultProps = { dispute: null, canOpen: false, onChanged: () => {} };

export default withStyles(s)(DisputePanel);
